import React, { useState } from "react";
import pluralize from "pluralize";
import { BetComboIcon as BetCombo } from "@picklebet/ui";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import type { PossibleCombination } from "@/features/betslip/types";
import { BetCard } from "@/features/betslip/components/cards/BetCard";
import { StakeInput } from "@/features/betslip/components/parts/StakeInput";
import { ComboAlert } from "@/features/betslip/components/parts/ComboAlert";
import { BetCardHeader } from "@/features/betslip/components/parts/BetCardHeader";
import { ComboGroup } from "@/features/betslip/components/parts/ComboGroup";
import { StakeStrip } from "@/features/betslip/components/parts/StakeStrip";
import { ComboQuickStakeChips } from "@/features/betslip/components/parts/ComboQuickStakeChips";
import { PayoutValue } from "@/features/betslip/components/parts/PayoutValue";
import { BonusCashPill } from "@/features/betslip/components/parts/tokens/BonusCashPill";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import { useHasPermission } from "@/features/auth/hooks/usePermission";
import { triggerHaptic } from "@/utilities/haptics";
import { getComboLabel } from "@/utilities/sharedBettingUtilities";

import * as promoStyles from "@/features/betslip/components/parts/tokens/PromoPills.module.scss";

type ComboRowProps = {
  combo: PossibleCombination;
};

const ComboRow = ({ combo }: ComboRowProps) => {
  const stake = useBetslipStore((s) => s.stakes.combinations[combo.type]);
  const comboPayout = useBetslipStore((s) => s.comboPayouts[combo.type] ?? 0);
  const comboPayoutsTbd = useBetslipStore((s) => s.comboPayoutsTbd);
  const isPlacing = useBetslipStore((s) => s.isPlacing);
  const isStakeDisabled = useBetslipStore((s) => s.combosStakeDisabled);
  const isReviewing = useBetslipStore((s) => s.isReviewing);
  // A blocked leg sits in every combination, so nothing here can be priced.
  const setCombinationStake = useBetslipStore((s) => s.setCombinationStake);
  const remainingPromoBalance = useRemainingPromoBalance();
  const maxPromoStakeCents = useRemainingPromoBalance({
    type: "combination",
    comboType: combo.type,
  });
  const hasPromoComboPermission = useHasPermission("submitPromoComboMultiBet");
  const [activeInput, setActiveInput] = useState<"total" | "perCombo" | null>(
    null,
  );

  const perCombo = stake?.perCombo ?? 0;
  const total = stake?.total ?? 0;
  const isPromo = stake?.isPromo ?? false;
  const correlationId = stake?.correlationId ?? "";

  const writeStake = (perComboCents: number, promo: boolean) =>
    setCombinationStake(combo.type, {
      perCombo: perComboCents,
      total: perComboCents * combo.count,
      isPromo: promo,
      correlationId,
    });

  const handlePerCombo = (perComboCents: number) => {
    const nextTotal = perComboCents * combo.count;
    // Drop Bonus Cash if the new total no longer fits the remaining balance
    // (legacy maybeDisablePromo).
    const keepPromo = isPromo && remainingPromoBalance + total >= nextTotal;
    writeStake(perComboCents, keepPromo);
  };

  // Legacy setCombinationTotalStake: keep the typed total, floor the
  // per-combo stake down to whole cents.
  const handleTotal = (totalCents: number) => {
    const keepPromo = isPromo && remainingPromoBalance + total >= totalCents;
    setCombinationStake(combo.type, {
      perCombo: Math.floor(totalCents / combo.count),
      total: totalCents,
      isPromo: keepPromo,
      correlationId,
    });
  };

  // Legacy syncCombinationsTotalStake: on blur snap the total back to a
  // clean perCombo × count; a sub-cent total becomes 1c per combo.
  const handleTotalBlur = () => {
    if (total > 0 && perCombo === 0) {
      writeStake(1, isPromo);
      return;
    }
    if (total !== perCombo * combo.count) {
      writeStake(perCombo, isPromo);
    }
  };

  const togglePromo = () => {
    triggerHaptic.click();
    writeStake(perCombo, !isPromo);
  };

  const isReviewingOrPlacing = isReviewing || isPlacing;
  const showBonusCash =
    hasPromoComboPermission &&
    ((!isReviewingOrPlacing &&
      remainingPromoBalance > 0 &&
      remainingPromoBalance >= total) ||
      isPromo);

  return (
    <BetCard
      testId={`combo-${combo.type}`}
      alert={<ComboAlert type={combo.type} count={combo.count} />}
    >
      <BetCardHeader
        icon={<BetCombo />}
        title={getComboLabel(Number(combo.type))}
        testId={`header-combo-${combo.type}`}
      />
      <ComboGroup
        variant="combo"
        label={pluralize("Combo", combo.count, true)}
        testId={`combo-count-${combo.type}`}
      />
      <StakeStrip
        testId={`combo-stake-strip-${combo.type}`}
        promoActive={isPromo}
        labels={["Total Stake", "Stake/Combo", "Est. Payout"]}
        cellVariants={["field", "field", "plain"]}
        leftSlot={
          <StakeInput
            value={total}
            onChange={handleTotal}
            onFocus={() => setActiveInput("total")}
            onBlur={() => {
              handleTotalBlur();
              setActiveInput(null);
            }}
            disabled={isStakeDisabled}
            testId={`combo-total-${combo.type}`}
          />
        }
        middleSlot={
          <StakeInput
            value={perCombo}
            onChange={handlePerCombo}
            onFocus={() => setActiveInput("perCombo")}
            onBlur={() => {
              handleTotalBlur();
              setActiveInput(null);
            }}
            disabled={isStakeDisabled}
            testId={`combo-stake-${combo.type}`}
          />
        }
        rightSlot={
          <PayoutValue
            dollars={comboPayout / 100}
            tbd={comboPayoutsTbd}
            testId={`combo-payout-${combo.type}`}
          />
        }
        quickStake={
          <ComboQuickStakeChips
            isOpen={activeInput !== null && !isStakeDisabled}
            activeInput={activeInput}
            totalCents={total}
            perComboCents={perCombo}
            onSetTotalCents={handleTotal}
            onSetPerComboCents={handlePerCombo}
            isPromo={isPromo}
            maxPromoStakeCents={maxPromoStakeCents}
          />
        }
      />
      {showBonusCash && (
        <div className={promoStyles.cardPromos}>
          <div className={promoStyles.pillRow}>
            <BonusCashPill
              active={isPromo}
              onToggle={togglePromo}
              disabled={isReviewingOrPlacing}
            />
          </div>
        </div>
      )}
    </BetCard>
  );
};

export const CombinationsCard = () => {
  const possibleCombinations = useBetslipStore((s) => s.possibleCombinations);

  if (!possibleCombinations || possibleCombinations.length === 0) return null;

  return (
    <>
      {possibleCombinations.map((combo) => (
        <ComboRow key={combo.type} combo={combo} />
      ))}
    </>
  );
};
